import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, CheckCheck, FileSignature, Inbox, ShoppingBag, ThumbsUp } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, fmtDateTime } from "@/lib/api";
import { PushToggle } from "@/components/PushToggle";

const ICONS = { richiesta: Inbox, preventivo: ThumbsUp, firma: FileSignature, extra: ShoppingBag };

export function NotificationBell() {
  const [data, setData] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);
  const seen = useRef(null);
  const nav = useNavigate();
  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      const fresh = r.data.items.filter((x) => !x.read && seen.current && !seen.current.has(x.id));
      fresh.slice(0, 3).forEach((x) => toast(x.title, { description: x.text, position: "bottom-right" }));
      seen.current = new Set(r.data.items.map((x) => x.id));
      setData(r.data);
    } catch {
      /* offline: keep last state */
    }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, "");
    document.title = data.unread ? `(${data.unread}) ${base}` : base;
  }, [data.unread]);
  const go = async (x) => {
    setOpen(false);
    if (!x.read) await api.post(`/notifications/${x.id}/read`);
    load();
    if (x.link) nav(x.link);
  };
  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-full text-[#47606B] hover:bg-[#EAF5F7]" title="Notifiche" data-testid="notif-bell">
          <Bell size={17} className={data.unread ? "text-[#0B6E8F]" : ""} />
          {data.unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E0533D] text-white text-[10px] font-semibold flex items-center justify-center animate-pulse" data-testid="notif-count">{data.unread > 99 ? "99+" : data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#D4E6EC]">
          <div className="font-serif-it text-lg font-semibold">Notifiche</div>
          {data.unread > 0 && <button onClick={readAll} className="text-xs text-[#0B6E8F] inline-flex items-center gap-1 hover:underline" data-testid="notif-read-all"><CheckCheck size={13} /> Segna tutte lette</button>}
        </div>
        <PushToggle />
        <div className="max-h-[60vh] overflow-y-auto">
          {data.items.length === 0 && <div className="p-6 text-sm text-center text-[#7B929C]" data-testid="notif-empty">Nessuna notifica per ora.</div>}
          {data.items.map((x) => {
            const Icon = ICONS[x.kind] || Bell;
            return (
              <button key={x.id} onClick={() => go(x)} className={`w-full text-left flex gap-3 px-4 py-3 border-b border-[#EEF4F6] hover:bg-[#F5FAFB] transition-colors ${x.read ? "" : "bg-[#EAF5F7]/60"}`} data-testid={`notif-item-${x.id}`}>
                <Icon size={16} className="mt-0.5 shrink-0 text-[#0B6E8F]" />
                <div className="min-w-0 flex-1">
                  <div className={`text-sm ${x.read ? "text-[#47606B]" : "font-semibold text-[#0D2B3A]"}`}>{x.title}</div>
                  {x.text && <div className="text-xs text-[#7B929C] truncate">{x.text}</div>}
                  <div className="text-[10px] text-[#A8C6CF] mt-0.5">{fmtDateTime(x.created_at)}</div>
                </div>
                {!x.read && <span className="w-2 h-2 rounded-full bg-[#E0533D] mt-1.5 shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
